// The Modify tools' geometry — Offset, Trim / Extend, Fillet / Chamfer — as pure
// functions over a ring: a polygon's outline or a wall's / line's centre line,
// closed or open by ONE rule (`ringOf`). Each gives the new rings and where every
// old vertex went (`RingEdit`), so what is numbered by them follows (anchors.ts
// `moveVertices`, ringRenumber.ts). DOM-free; ui/modifyTools.ts writes the result
// into the drawing (docs/claude/tools.md, *Modify tools*).
import type { Vec2 } from '../shared/types';
import {
  cornerFillet,
  distToSegment,
  lineLineIntersect,
  offsetPolyline,
} from './geometry';
import { polylineIsClosed } from './wallTool';

/** A ring of vertices: closed when its last vertex joins its first (no point
 *  repeated). */
export interface Ring { pts: Vec2[]; closed: boolean }

/** Where an old vertex went: onto result ring `ring` (0: the object itself, 1: a
 *  new piece), at `vertex`. */
export interface RingTo { ring: number; vertex: number }
/** A ring's change: the rings it became, and where each old vertex went (null:
 *  gone). */
export interface RingEdit { rings: Ring[]; map: (v: number) => RingTo | null }

const copy = (p: Vec2): Vec2 => ({ x: p.x, y: p.y });
/** Stations closer than this (in sides) are one place. */
const SAME = 1e-7;

/** The ring a polygon or a wall / line is drawn on — one rule for every Modify
 *  tool: a polygon's is closed; a line's is closed when its ends meet, which it
 *  stores by repeating its first point last (the ring leaves the repeat out). */
export function ringOf(points: readonly Vec2[], isWall: boolean): Ring {
  if (!isWall) return { pts: points.map(copy), closed: true };
  if (polylineIsClosed(points)) return { pts: points.slice(0, -1).map(copy), closed: true };
  return { pts: points.map(copy), closed: false };
}

/** The points a polygon or a wall / line stores for a ring (`ringOf` undone). */
export function storedPoints(r: Ring, isWall: boolean): Vec2[] {
  return isWall && r.closed ? [...r.pts, copy(r.pts[0])] : r.pts;
}

/** An object's stored vertex as its ring's vertex (a closed line's repeat is its
 *  first). */
export function ringVertex(points: readonly Vec2[], isWall: boolean, v: number): number {
  return isWall && v === points.length - 1 && polylineIsClosed(points) ? 0 : v;
}

const sidesOf = (r: Ring): number => (r.closed ? r.pts.length : r.pts.length - 1);

/** The point at station `s` along a ring (side + fraction along it). */
function pointAt(r: Ring, s: number): Vec2 {
  const n = r.pts.length;
  if (r.closed) s = ((s % n) + n) % n;
  const side = Math.max(0, Math.min(sidesOf(r) - 1, Math.floor(s)));
  const t = s - side, a = r.pts[side], b = r.pts[(side + 1) % n];
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}

/** The side of a ring nearest `p`, and how far along it `p` falls (0..1). */
function nearestSide(r: Ring, p: Vec2): { side: number; t: number } {
  let best = { side: 0, t: 0 }, bestD = Infinity;
  for (let i = 0; i < sidesOf(r); i++) {
    const a = r.pts[i], b = r.pts[(i + 1) % r.pts.length];
    const dx = b.x - a.x, dy = b.y - a.y, L2 = dx * dx + dy * dy;
    const t = L2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / L2)) : 0;
    const d = Math.hypot(a.x + dx * t - p.x, a.y + dy * t - p.y);
    if (d < bestD) { bestD = d; best = { side: i, t }; }
  }
  return best;
}

/** Least distance from `p` to any side of a polyline (closed: its last vertex
 *  joins its first). */
export function distToPolyline(p: Vec2, pts: readonly Vec2[], closed: boolean): number {
  let d = Infinity;
  const segs = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < segs; i++) d = Math.min(d, distToSegment(p, pts[i], pts[(i + 1) % pts.length]));
  return d;
}

/** The run of a ring from station `a` to station `b` (forward; round a closed
 *  ring's joint), with where each old vertex in it went. */
function run(r: Ring, a: number, b: number): { pts: Vec2[]; of: Map<number, number> } {
  const n = r.pts.length;
  const span = r.closed ? (((b - a) % n) + n) % n : b - a;
  const pts: Vec2[] = [];
  const of = new Map<number, number>();
  const put = (s: number): void => {
    const k = Math.round(s);
    if (Math.abs(s - k) < SAME) {
      const v = r.closed ? ((k % n) + n) % n : k;
      if (!of.has(v)) of.set(v, pts.length);
      pts.push(copy(r.pts[v]));
    } else pts.push(pointAt(r, s));
  };
  put(a);
  for (let k = Math.floor(a + SAME) + 1; k < a + span - SAME; k++) put(k);
  put(a + span);
  return { pts, of };
}

/** The rings made of runs, with the map their vertices give. */
function fromRuns(runs: Array<{ pts: Vec2[]; of: Map<number, number> }>, closed: boolean): RingEdit {
  return {
    rings: runs.map((p) => ({ pts: p.pts, closed })),
    map: (v) => {
      for (let i = 0; i < runs.length; i++) { const k = runs[i].of.get(v); if (k != null) return { ring: i, vertex: k }; }
      return null;
    },
  };
}

/** The ring with some of its vertices moved, each staying where it is numbered. */
function moved(r: Ring, at: ReadonlyMap<number, Vec2>): RingEdit {
  return { rings: [{ pts: r.pts.map((p, i) => copy(at.get(i) ?? p)), closed: r.closed }], map: (v) => ({ ring: 0, vertex: v }) };
}

const length = (pts: readonly Vec2[]): number => pts.reduce((s, p, i) => (i ? s + Math.hypot(p.x - pts[i - 1].x, p.y - pts[i - 1].y) : 0), 0);

// ---- Offset ----

/** OFFSET: a copy of the ring at distance `dist` (typed), else at the cursor's
 *  distance from it ("Through"), on the side nearer the cursor. Null when that
 *  distance is nil. */
export function offsetToward(r: Ring, cursor: Vec2, dist: number | null): Ring | null {
  const d = dist ?? distToPolyline(cursor, r.pts, r.closed);
  if (d < 1e-3) return null;
  const plus = offsetPolyline(r.pts, d, r.closed), minus = offsetPolyline(r.pts, -d, r.closed);
  return { pts: distToPolyline(cursor, plus, r.closed) <= distToPolyline(cursor, minus, r.closed) ? plus : minus, closed: r.closed };
}

// ---- Trim / Extend ----

/** Where side a→b crosses side c→d: the fraction along a→b and the point. */
function crossing(a: Vec2, b: Vec2, c: Vec2, d: Vec2): { t: number; p: Vec2 } | null {
  const rx = b.x - a.x, ry = b.y - a.y, sx = d.x - c.x, sy = d.y - c.y;
  const den = rx * sy - ry * sx;
  if (Math.abs(den) < 1e-12) return null;
  const t = ((c.x - a.x) * sy - (c.y - a.y) * sx) / den;
  const u = ((c.x - a.x) * ry - (c.y - a.y) * rx) / den;
  if (t < -1e-9 || t > 1 + 1e-9 || u < -1e-9 || u > 1 + 1e-9) return null;
  const tt = Math.max(0, Math.min(1, t));
  return { t: tt, p: { x: a.x + rx * tt, y: a.y + ry * tt } };
}

/** Every place ring `r` crosses `cutter`, by station along `r`, in order. */
function crossings(r: Ring, cutter: Ring): Array<{ at: number; p: Vec2 }> {
  const n = r.pts.length, cn = cutter.pts.length;
  const out: Array<{ at: number; p: Vec2 }> = [];
  for (let i = 0; i < sidesOf(r); i++) {
    for (let j = 0; j < sidesOf(cutter); j++) {
      const x = crossing(r.pts[i], r.pts[(i + 1) % n], cutter.pts[j], cutter.pts[(j + 1) % cn]);
      if (x) out.push({ at: r.closed ? (i + x.t) % n : i + x.t, p: x.p });
    }
  }
  out.sort((x, y) => x.at - y.at);
  return out.filter((x, i) => i === 0 || x.at - out[i - 1].at > SAME);
}

/**
 * TRIM: the part of ring `r` clicked at `at` between the crossings with `cutter`
 * around it goes. An open ring loses it from the crossing before to the crossing
 * after (to its end where there is none), so a part cut out of the middle leaves
 * two pieces; a closed one loses it between the two crossings around the click,
 * a polygon (`area`) closed again straight across them, a line opened there. With
 * no crossing it is EXTEND (`extendRing`). Null: nothing to trim.
 */
export function trimRing(r: Ring, at: Vec2, cutter: Ring, area: boolean): RingEdit | null {
  const xs = crossings(r, cutter);
  if (!xs.length) return extendRing(r, at, cutter);
  const near = nearestSide(r, at);
  const sc = near.side + near.t;
  const before = xs.filter((x) => x.at < sc - SAME), after = xs.filter((x) => x.at > sc + SAME);
  if (!r.closed) {
    const lo = before[before.length - 1], hi = after[0];
    const runs = [lo && run(r, 0, lo.at), hi && run(r, hi.at, r.pts.length - 1)]
      .filter((p): p is ReturnType<typeof run> => !!p && length(p.pts) > 1e-6);
    return runs.length ? fromRuns(runs, false) : null;
  }
  if (xs.length < 2) return null;
  const lo = before[before.length - 1] ?? xs[xs.length - 1], hi = after[0] ?? xs[0];
  if (lo === hi) return null;
  const kept = run(r, hi.at, lo.at);
  if (kept.pts.length < (area ? 3 : 2) || length(kept.pts) < 1e-6) return null;
  return fromRuns([kept], area);
}

/** EXTEND: the end of an open ring's first or last side — the side clicked, its
 *  end nearer the click on a single side — runs on along its line to the nearest
 *  place it meets `cutter`. Null for a closed ring, an inner side, or a line that
 *  meets nothing ahead. */
export function extendRing(r: Ring, at: Vec2, cutter: Ring): RingEdit | null {
  const n = r.pts.length;
  if (r.closed || n < 2) return null;
  const near = nearestSide(r, at);
  const ends = [near.side === 0 ? 0 : -1, near.side === n - 2 ? n - 1 : -1].filter((e) => e >= 0);
  if (!ends.length) return null;
  const end = ends.length === 2 ? (near.t < 0.5 ? 0 : n - 1) : ends[0];
  const A = r.pts[end === 0 ? 1 : n - 2], B = r.pts[end];
  const dir = { x: B.x - A.x, y: B.y - A.y };
  let best: Vec2 | null = null, bestT = Infinity;
  for (let j = 0; j < sidesOf(cutter); j++) {
    const c = cutter.pts[j], d = cutter.pts[(j + 1) % cutter.pts.length];
    const cd = { x: d.x - c.x, y: d.y - c.y };
    const X = lineLineIntersect(A, dir, c, cd);
    if (!X) continue;
    const u = Math.abs(cd.x) > Math.abs(cd.y) ? (X.x - c.x) / cd.x : (X.y - c.y) / cd.y;
    if (u < -1e-9 || u > 1 + 1e-9) continue;
    const L2 = dir.x * dir.x + dir.y * dir.y;
    const t = L2 ? ((X.x - A.x) * dir.x + (X.y - A.y) * dir.y) / L2 : 0;
    if (t > 1 + 1e-9 && t < bestT) { bestT = t; best = X; }
  }
  return best ? moved(r, new Map([[end, best]])) : null;
}

// ---- Fillet / Chamfer ----

/** A side picked for a fillet: the ring, where it was clicked, and whether it is a
 *  polygon's (`area`: it stays closed, an arc drawn over its new side). */
export interface FilletPick { ring: Ring; at: Vec2; area: boolean }
/** A rounding arc, counter-clockwise from `startDeg` to `endDeg`. */
export interface FilletArc { center: Vec2; radius: number; startDeg: number; endDeg: number }
export interface FilletResult {
  /** The first pick's ring, changed. */
  first: RingEdit;
  /** The second's; null when both picks are on one ring (`first` holds both). */
  second: RingEdit | null;
  arc: FilletArc | null;
  /** A straight bevel to add between two rings (a chamfer across two objects). */
  bevel: [Vec2, Vec2] | null;
}

const unit = (v: Vec2): Vec2 | null => { const L = Math.hypot(v.x, v.y); return L < 1e-9 ? null : { x: v.x / L, y: v.y / L }; };
const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);

/** The minor arc about `center` from `a` to `b`, counter-clockwise. */
function arcBetween(center: Vec2, radius: number, a: Vec2, b: Vec2): FilletArc {
  let a1 = (Math.atan2(a.y - center.y, a.x - center.x) * 180) / Math.PI;
  let a2 = (Math.atan2(b.y - center.y, b.x - center.x) * 180) / Math.PI;
  let sweep = a2 - a1;
  while (sweep <= -180) sweep += 360;
  while (sweep > 180) sweep -= 360;
  if (sweep < 0) [a1, a2] = [a2, a1];
  return { center, radius, startDeg: a1, endDeg: a2 };
}

/**
 * FILLET (`radius` > 0: a true arc) or CHAMFER (`chamfer`: a straight bevel set
 * back `radius` from the corner; 0: a sharp corner) where the lines of the two
 * sides picked meet. Two sides meeting at one vertex of one ring (`sameRing`)
 * swap it for the two tangent points — a rounded line split in two around its
 * arc, a closed line opened there. Otherwise each side's end toward the corner
 * goes to its point: an open ring's end vertex moves (cut back or run on), an
 * inner vertex cuts the ring there and keeps the far side (only inside the side),
 * a closed ring's vertex moves. Null: parallel, collinear, too big for the sides,
 * or a cut past an inner vertex.
 */
export function fillet(a: FilletPick, b: FilletPick, sameRing: boolean, radius: number, chamfer: boolean): FilletResult | null {
  const side = (p: FilletPick): { i: number; a: Vec2; b: Vec2; n: number } => {
    const s = nearestSide(p.ring, p.at), n = p.ring.pts.length;
    return { i: s.side, a: p.ring.pts[s.side], b: p.ring.pts[(s.side + 1) % n], n };
  };
  const s1 = side(a), s2 = side(b);
  const C = lineLineIntersect(s1.a, { x: s1.b.x - s1.a.x, y: s1.b.y - s1.a.y }, s2.a, { x: s2.b.x - s2.a.x, y: s2.b.y - s2.a.y });
  if (!C) return null;
  const nearA1 = dist(C, s1.a) < dist(C, s1.b), nearA2 = dist(C, s2.a) < dist(C, s2.b);
  const near1 = nearA1 ? s1.i : (s1.i + 1) % s1.n, far1 = nearA1 ? s1.b : s1.a;
  const near2 = nearA2 ? s2.i : (s2.i + 1) % s2.n, far2 = nearA2 ? s2.b : s2.a;
  const u1 = unit({ x: far1.x - C.x, y: far1.y - C.y }), u2 = unit({ x: far2.x - C.x, y: far2.y - C.y });
  if (!u1 || !u2) return null;
  const phi = Math.acos(Math.max(-1, Math.min(1, u1.x * u2.x + u1.y * u2.y)));
  if (phi < 1e-3 || Math.PI - phi < 1e-3) return null;
  const r = Math.max(0, radius);
  let T1: Vec2, T2: Vec2, arc: FilletArc | null = null;
  if (r > 0 && !chamfer) {
    const f = cornerFillet(far1, C, far2, r);
    if (!f) return null;
    T1 = f.t1; T2 = f.t2;
    if (dist(T1, T2) >= 1e-6) arc = arcBetween(f.center, r, T1, T2);
  } else {
    const L = chamfer ? r : 0;
    T1 = { x: C.x + u1.x * L, y: C.y + u1.y * L };
    T2 = { x: C.x + u2.x * L, y: C.y + u2.y * L };
  }
  if (dist(C, T1) > dist(C, far1) + 1e-6 || dist(C, T2) > dist(C, far2) + 1e-6) return null;
  const bevel: [Vec2, Vec2] | null = !arc && chamfer && dist(T1, T2) >= 1e-6 ? [T1, T2] : null;

  if (sameRing && near1 === near2) {
    const ring = a.ring, n = ring.pts.length, k = near1, pts = ring.pts;
    const pred = pts[(k - 1 + n) % n];
    const t1IsPred = dist(far1, pred) <= dist(far2, pred);
    const Tp = t1IsPred ? T1 : T2, Ts = t1IsPred ? T2 : T1;
    let first: RingEdit;
    if (arc && !a.area && !ring.closed) {
      first = {
        rings: [{ pts: [...pts.slice(0, k).map(copy), Tp], closed: false }, { pts: [Ts, ...pts.slice(k + 1).map(copy)], closed: false }],
        map: (v) => (v < k ? { ring: 0, vertex: v } : v === k ? null : { ring: 1, vertex: v - k }),
      };
    } else if (arc && !a.area) {
      const rest = Array.from({ length: n - 1 }, (_, j) => copy(pts[(k + 1 + j) % n]));
      first = { rings: [{ pts: [Ts, ...rest, Tp], closed: false }], map: (v) => (v === k ? null : { ring: 0, vertex: ((v - k - 1 + n) % n) + 1 }) };
    } else {
      first = {
        rings: [{ pts: [...pts.slice(0, k).map(copy), Tp, Ts, ...pts.slice(k + 1).map(copy)], closed: ring.closed }],
        map: (v) => (v === k ? null : { ring: 0, vertex: v < k ? v : v + 1 }),
      };
    }
    return { first, second: null, arc, bevel: null };
  }

  /** The side's end toward the corner goes to `T`. */
  const cut = (p: FilletPick, s: { i: number; n: number }, near: number, T: Vec2): RingEdit | null => {
    const ring = p.ring;
    if (ring.closed || near === 0 || near === s.n - 1) return moved(ring, new Map([[near, T]]));
    const farIdx = near === s.i ? s.i + 1 : s.i;
    if (dist(ring.pts[farIdx], T) > dist(ring.pts[farIdx], ring.pts[near]) + 1e-6) return null;
    const A = ring.pts[s.i], B = ring.pts[s.i + 1];
    const L2 = (B.x - A.x) ** 2 + (B.y - A.y) ** 2;
    const sT = s.i + (L2 ? Math.max(0, Math.min(1, ((T.x - A.x) * (B.x - A.x) + (T.y - A.y) * (B.y - A.y)) / L2)) : 0);
    return fromRuns([farIdx > near ? run(ring, sT, s.n - 1) : run(ring, 0, sT)], false);
  };
  if (sameRing) {
    const ends = (v: number): boolean => a.ring.closed || v === 0 || v === s1.n - 1;
    if (!ends(near1) || !ends(near2)) return null;
    return { first: moved(a.ring, new Map([[near1, T1], [near2, T2]])), second: null, arc, bevel };
  }
  const first = cut(a, s1, near1, T1), second = cut(b, s2, near2, T2);
  return first && second ? { first, second, arc, bevel } : null;
}
